import { GameScene, getOverheadTextY, type PlayerRenderObjects } from '../GameScene';
import { getTalkOffset } from '@shared/types/bodyConfig';
import { TILE_SIZE } from '@shared/constants';

/**
 * Texto flotante overhead (capa cosmética que sigue a las entidades por frame): globos de chat de
 * players y NPCs, "+HP/+Mana" de curas, números de daño, palabras mágicas del casteo y el error
 * rojo self-only. `updateChatBubblesPositions()` (llamada desde el update loop) reposiciona todo con
 * pixel-snap para evitar shimmer al caminar. Extraído de GameScene (god-object). Los 6 Maps de estado
 * y el static DAMAGE_HEAL_X_OFFSET viven en GameScene (host) y se acceden vía `this.scene.*`.
 */
export class OverheadTextController {
  constructor(private scene: GameScene) {}

  public showChatBubble(playerId: string, message: string, color: string = '#ffffff'): void {
    const render = this.scene.playerObjects.get(playerId);

    if (!render) return;

    // Si ya había una burbuja activa para este jugador, la reemplazamos

    const existing = this.scene.activeChatBubbles.get(playerId);

    if (existing) {
      existing.destroy();

      this.scene.activeChatBubbles.delete(playerId);
    }

    const maxWidth = TILE_SIZE * 5; // ~4–5 tiles de ancho

    // Posición base: usar altura real del sprite renderizado + talkOffset configurable
    const bodyId = render.bodyId;
    // Obtener altura directamente del sprite (más robusto que el caché)
    const bodyHeight = render.sprite.displayHeight;
    const talkOffset = getTalkOffset(bodyId);

    // El sprite tiene origin(0.5, 1), así que sprite.y es la base de los pies
    // El texto va arriba del body completo - el talkOffset (negativo = más arriba)
    const baseY = getOverheadTextY(render.sprite.y, bodyHeight, talkOffset);

    const bubbleText = this.scene.addText(render.sprite.x, baseY, message, {
        fontSize: '20px', // legibilidad: globo de chat (player + NPC)

        color,

        wordWrap: { width: maxWidth },

        align: 'center',
      })

      .setOrigin(0.5, 1)

      .setDepth(600); // por encima de los personajes

    // Offset vertical relativo (0 = posición base)

    (bubbleText as any).offsetY = 0;

    // Guardar referencia para seguir al jugador en update

    this.scene.activeChatBubbles.set(playerId, bubbleText);

    // Duraciones de animación: ~2.5s en total (subida + hold + bajada/fade)

    const upDuration = 150;

    const holdDuration = 2000;

    const downDuration = 350;

    // 1) Slide up suave

    this.scene.tweens.add({
      targets: bubbleText as any,

      offsetY: -TILE_SIZE * 0.3,

      duration: upDuration,

      ease: 'Sine.easeOut',
    });

    // 2) Después del hold, slide down + fade out y destrucción

    this.scene.time.delayedCall(upDuration + holdDuration, () => {
      if (!bubbleText.active) return;

      this.scene.tweens.add({
        targets: bubbleText as any,

        offsetY: 0,

        alpha: 0,

        duration: downDuration,

        ease: 'Sine.easeIn',

        onComplete: () => {
          if (!bubbleText.active) return;

          bubbleText.destroy();

          const current = this.scene.activeChatBubbles.get(playerId);

          if (current === bubbleText) {
            this.scene.activeChatBubbles.delete(playerId);
          }
        },
      });
    });
  }

  public showNpcChatBubble(npcId: string, message: string, color: string = '#ffffff'): void {
    const render = this.scene.npcObjects.get(npcId);

    if (!render) return;

    // Si ya había una burbuja activa para este NPC, la reemplazamos

    const existing = this.scene.activeNpcChatBubbles.get(npcId);

    if (existing) {
      existing.destroy();

      this.scene.activeNpcChatBubbles.delete(npcId);
    }

    const maxWidth = TILE_SIZE * 5; // ~4–5 tiles de ancho

    // Posición base: usar altura real del sprite renderizado + talkOffset configurable
    const bodyId = render.bodyId;
    // Obtener altura directamente del sprite (más robusto que el caché)
    const bodyHeight = render.sprite.displayHeight;
    const talkOffset = getTalkOffset(bodyId);

    // El sprite tiene origin(0.5, 1), así que sprite.y es la base de los pies
    // El texto va arriba del body completo - el talkOffset (negativo = más arriba)
    const baseY = getOverheadTextY(render.sprite.y, bodyHeight, talkOffset);

    const bubbleText = this.scene.addText(render.sprite.x, baseY, message, {
        fontSize: '20px', // legibilidad: globo de chat (player + NPC)

        color,

        wordWrap: { width: maxWidth },

        align: 'center',
      })

      .setOrigin(0.5, 1)

      .setDepth(600); // por encima de los personajes

    // Offset vertical relativo (0 = posición base)

    (bubbleText as any).offsetY = 0;

    // Guardar referencia para seguir al NPC en update

    this.scene.activeNpcChatBubbles.set(npcId, bubbleText);

    // Duraciones de animación: ~2.5s en total (subida + hold + bajada/fade)

    const upDuration = 150;

    const holdDuration = 2000;

    const downDuration = 350;

    // 1) Slide up suave

    this.scene.tweens.add({
      targets: bubbleText as any,

      offsetY: -TILE_SIZE * 0.3,

      duration: upDuration,

      ease: 'Sine.easeOut',
    });

    // 2) Después del hold, slide down + fade out y destrucción

    const startFade = (): void => {
      if (!bubbleText.active) return;

      this.scene.tweens.add({
        targets: bubbleText as any,

        offsetY: 0,

        alpha: 0,

        duration: downDuration,

        ease: 'Sine.easeIn',

        onComplete: () => {
          if (!bubbleText.active) return;

          bubbleText.destroy();

          const current = this.scene.activeNpcChatBubbles.get(npcId);

          if (current === bubbleText) {
            this.scene.activeNpcChatBubbles.delete(npcId);
          }
        },
      });
    };

    // Si al terminar el hold normal seguimos con el NPC en focus (targeteado),
    // extendemos el hold otro tanto (dura el doble). Si perdimos el focus,
    // el fade arranca en el tiempo normal.
    this.scene.time.delayedCall(upDuration + holdDuration, () => {
      if (!bubbleText.active) return;

      if (this.scene.targets.selectedTargetId === npcId) {
        this.scene.time.delayedCall(holdDuration, startFade);
      } else {
        startFade();
      }
    });
  }

  public showHealthManaGainText(
    entityId: string,
    amount: number,
    isHealth: boolean,
    isPlayer: boolean
  ): void {
    const render = isPlayer ? this.scene.playerObjects.get(entityId) : this.scene.npcObjects.get(entityId);
    if (!render) return;

    const bodyId = render.bodyId;
    const bodyHeight = render.sprite.displayHeight;
    
    // Posición inicial: desde aproximadamente el centro del tile o un poco más abajo
    // El sprite tiene origin(0.5, 1), así que sprite.y es la base de los pies (bottom del sprite)
    // Para que aparezca más abajo, cerca del centro del tile, necesitamos calcular desde sprite.y
    // sprite.y está en los pies, así que para el centro-bajo del body, usamos una fracción pequeña
    // Queremos que aparezca más cerca de los pies, así que usamos un porcentaje pequeño desde sprite.y hacia arriba
    
    // Calcular posición base: sprite.y es la base (pies)
    // Para que aparezca cerca del centro del tile o desde donde empieza verticalmente el tile,
    // subimos solo unos pocos píxeles desde los pies (por ejemplo, 8-12 píxeles)
    // Esto lo coloca en la parte muy baja del body, cerca del inicio del tile
    const baseY = render.sprite.y - 10; // Solo 10 píxeles desde los pies (parte muy baja del body, cerca del centro del tile)
    
    // Color: verde para HP, azul/celeste para Mana
    const color = isHealth ? '#00ff00' : '#87ceeb';
    
    // Curas/ganancias van a la DERECHA (el daño va a la izquierda).
    const xOffset = GameScene.DAMAGE_HEAL_X_OFFSET;

    // Crear texto "+XXX" - mitad de tamaño (7px en vez de 14px)
    // Empezar desde baseY (sin offset adicional inicial)
    const gainText = this.scene.addText(render.sprite.x + xOffset, baseY, `+${Math.floor(amount)}`, {
      fontSize: '8px',
      color: color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    })
      .setOrigin(0.5, 1)
      .setDepth(600); // Por encima de los personajes

    // Offset vertical relativo (empezamos en 0, subimos continuamente)
    (gainText as any).offsetY = 0;
    // Offset horizontal fijo (derecha).
    (gainText as any).offsetX = xOffset;

    // Guardar referencia para seguir la entidad
    const trackingKey = isPlayer ? `player_${entityId}` : `npc_${entityId}`;
    
    // Si ya había un texto activo, destruirlo
    const existing = this.scene.activeHealthManaGainTexts.get(trackingKey);
    if (existing) {
      existing.destroy();
      this.scene.activeHealthManaGainTexts.delete(trackingKey);
    }
    
    // Guardar referencia a la entidad y si es player o npc
    (gainText as any).entityId = entityId;
    (gainText as any).isPlayer = isPlayer;
    this.scene.activeHealthManaGainTexts.set(trackingKey, gainText);

    // Animación: subir continuamente mientras se desvanece (todo en una sola animación)
    const totalDuration = 1200; // Duración total: 1.2 segundos
    // Subir el doble de alto: desde donde empieza (0) hasta -TILE_SIZE * 1.2 (más arriba)
    const finalOffsetY = -TILE_SIZE * 1.2; // Posición final: el doble de alto desde donde empieza

    // Una sola animación que sube continuamente mientras se desvanece
    this.scene.tweens.add({
      targets: gainText as any,
      offsetY: finalOffsetY,
      alpha: 0,
      duration: totalDuration,
      ease: 'Sine.easeOut',
      onComplete: () => {
        if (!gainText.active) return;
        gainText.destroy();
        const current = this.scene.activeHealthManaGainTexts.get(trackingKey);
        if (current === gainText) {
          this.scene.activeHealthManaGainTexts.delete(trackingKey);
        }
      },
    });

    // Actualizar posición mientras se anima (similar a updateChatBubblesPositions)
    // Esto se manejará en updateChatBubblesPositions también
  }

  public showDamageText(targetId: string, damage: number, isCrit: boolean, color?: string): void {
    // Funciona sobre players y mobs (igual que las curas).
    const isPlayer = this.scene.playerObjects.has(targetId);
    const render = isPlayer ? this.scene.playerObjects.get(targetId) : this.scene.npcObjects.get(targetId);
    if (!render) return;

    // Golpe sin daño (evasión/bloqueo total): palabra flotante "FALLÓ" en gris, en vez de "-0".
    if (Math.floor(damage) <= 0) {
      const miss = this.scene.addText(render.sprite.x, render.sprite.y - 18, 'FALLÓ', {
        fontSize: '9px',
        color: '#b8b8b8',
        stroke: '#000000',
        strokeThickness: 2,
      })
        .setOrigin(0.5)
        .setDepth(600);
      this.scene.tweens.add({
        targets: miss,
        y: miss.y - 34,
        alpha: 0,
        duration: 1000,
        ease: 'Quad.easeOut',
        onComplete: () => miss.destroy(),
      });
      return;
    }

    const label = `-${Math.max(0, Math.floor(damage))}`;
    // Tamaño del número según el % de vida que arrancó el golpe (los golpes gordos "gritan").
    const targetMaxHp = isPlayer
      ? ((this.scene.room?.state.players.get(targetId) as unknown as { maxHp?: number } | undefined)?.maxHp ?? 0)
      : ((this.scene.room?.state.npcs.get(targetId) as unknown as { maxHp?: number } | undefined)?.maxHp ?? 0);
    const hpFrac = targetMaxHp > 0 ? damage / targetMaxHp : 0;
    const dmgFontSize = hpFrac >= 0.3 ? '13px' : hpFrac >= 0.15 ? '10px' : '8px';

    // CRÍTICO: se mantiene aparte (amarillo, más grande), como antes.
    if (isCrit) {
      const text = this.scene.addText(render.sprite.x, render.sprite.y - 24, label, {
        fontSize: '16px',
        color: '#ffdd00',
        stroke: '#000000',
        strokeThickness: 2,
      })
        .setOrigin(0.5)
        .setDepth(900);

      this.scene.tweens.add({
        targets: text,
        y: text.y - 50,
        alpha: 0,
        duration: 1800,
        ease: 'Quad.easeOut',
        onComplete: () => text.destroy(),
      });
      return;
    }

    // DAÑO NORMAL: mismo sistema/estilo que las curas, pero ROJO y a la IZQUIERDA.
    // +1pt de tipografía si el daño supera 200.
    const xOffset = -GameScene.DAMAGE_HEAL_X_OFFSET;
    const baseY = render.sprite.y - 10;
    const text = this.scene.addText(render.sprite.x + xOffset, baseY, label, {
      fontSize: dmgFontSize,
      color: color ?? '#ff3b3b',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    })
      .setOrigin(0.5, 1)
      .setDepth(600);

    (text as any).offsetY = 0;
    (text as any).offsetX = xOffset;
    (text as any).entityId = targetId;
    (text as any).isPlayer = isPlayer;

    const trackingKey = isPlayer ? `dmg_player_${targetId}` : `dmg_npc_${targetId}`;
    const existing = this.scene.activeDamageTexts.get(trackingKey);
    if (existing) {
      existing.destroy();
      this.scene.activeDamageTexts.delete(trackingKey);
    }
    this.scene.activeDamageTexts.set(trackingKey, text);

    this.scene.tweens.add({
      targets: text as any,
      offsetY: -TILE_SIZE * 1.2,
      alpha: 0,
      duration: 1200,
      ease: 'Sine.easeOut',
      onComplete: () => {
        if (!text.active) return;
        text.destroy();
        if (this.scene.activeDamageTexts.get(trackingKey) === text) {
          this.scene.activeDamageTexts.delete(trackingKey);
        }
      },
    });
  }

  public showSpellWords(entityId: string, words: string): void {
    // Remove previous spell words for this entity
    const prev = this.scene.activeSpellWords.get(entityId);
    if (prev) { prev.destroy(); this.scene.activeSpellWords.delete(entityId); }

    // Buscar el render del entity: puede ser un player o un NPC.
    // Los NPCs hostiles que castean spells también muestran las palabras
    // mágicas arriba, igual que los players.
    const render =
      (this.scene.playerObjects.get(entityId) as { sprite: Phaser.GameObjects.Sprite; bodyId: number } | undefined) ??
      (this.scene.npcObjects.get(entityId) as { sprite: Phaser.GameObjects.Sprite; bodyId: number } | undefined);
    if (!render) return;

    const bodyHeight = render.sprite.displayHeight;
    const talkOffset = getTalkOffset(render.bodyId);
    const baseY = getOverheadTextY(render.sprite.y, bodyHeight, talkOffset);

    // Strip trailing exclamation marks
    const cleanWords = words.replace(/!+$/, '');
    const text = this.scene.addText(render.sprite.x, baseY, cleanWords, {
      fontSize: '20px', // legibilidad: palabras mágicas (mismo tamaño que chat/error)
      color: '#44bbcc', // dark cyan
      fontStyle: 'bold',
      wordWrap: { width: 160 },
      align: 'center',
      stroke: '#000000',
      strokeThickness: 1,
    })
      .setOrigin(0.5, 1)
      .setDepth(601);

    (text as any).offsetY = 0;
    this.scene.activeSpellWords.set(entityId, text);

    // Slide up → hold 4s (double of chat) → slide down + fade
    this.scene.tweens.add({ targets: text as any, offsetY: -TILE_SIZE * 0.3, duration: 150, ease: 'Sine.easeOut' });
    this.scene.time.delayedCall(150 + 4000, () => {
      if (!text.active) return;
      this.scene.tweens.add({
        targets: text as any, offsetY: 0, alpha: 0, duration: 350, ease: 'Sine.easeIn',
        onComplete: () => {
          if (text.active) text.destroy();
          if (this.scene.activeSpellWords.get(entityId) === text) this.scene.activeSpellWords.delete(entityId);
        },
      });
    });
  }

  public showSelfErrorText(msg: string): void {
    if (!this.scene.localSessionId) return;
    const entityId = this.scene.localSessionId;
    const render = this.scene.playerObjects.get(entityId);
    if (!render) return;

    // Remove previous error text
    const prev = this.scene.activeErrorTexts.get(entityId);
    if (prev) { prev.destroy(); this.scene.activeErrorTexts.delete(entityId); }

    const bh = render.sprite.displayHeight;
    const talkOffset = getTalkOffset(render.bodyId);
    const text = this.scene.addText(render.sprite.x, getOverheadTextY(render.sprite.y, bh, talkOffset), msg, {
      fontSize: '20px', // legibilidad: error rojo de acción (mismo tamaño que el chat)
      color: '#ff4444',
      align: 'center',
      stroke: '#000000',
      strokeThickness: 1,
    }).setOrigin(0.5, 1).setDepth(602);

    (text as any).offsetY = 0;
    this.scene.activeErrorTexts.set(entityId, text);

    // Same animation as chat: slide up → hold → slide down + fade
    this.scene.tweens.add({ targets: text as any, offsetY: -TILE_SIZE * 0.3, duration: 150, ease: 'Sine.easeOut' });
    this.scene.time.delayedCall(150 + 2000, () => {
      if (!text.active) return;
      this.scene.tweens.add({
        targets: text as any, offsetY: 0, alpha: 0, duration: 350, ease: 'Sine.easeIn',
        onComplete: () => {
          if (text.active) text.destroy();
          if (this.scene.activeErrorTexts.get(entityId) === text) this.scene.activeErrorTexts.delete(entityId);
        },
      });
    });
  }

  public updateChatBubblesPositions(): void {
    // Actualizar bubbles de jugadores

    if (this.scene.activeChatBubbles.size > 0) {
      this.scene.activeChatBubbles.forEach((text, playerId) => {
        const render = this.scene.playerObjects.get(playerId);

        if (!render || !text.active) {
          text.destroy();

          this.scene.activeChatBubbles.delete(playerId);

          return;
        }

        // Usar altura real del sprite - talkOffset (negativo = más arriba)
        const bodyHeight = render.sprite.displayHeight;
        const talkOffset = getTalkOffset(render.bodyId);

        // Pixel-snap X igual que el body/head/label (renderX = Math.round(currentWorldX)).
        // La cámara también va redondeada, así que usar el float crudo desalineaba el
        // texto sub-píxel cada frame y producía shimmer de las glifas al caminar horizontal.
        const baseY = getOverheadTextY(render.sprite.y, bodyHeight, talkOffset);

        const offsetY = (text as any).offsetY ?? 0;

        text.setPosition(Math.round(render.currentWorldX), Math.round(baseY + offsetY));
      });
    }

    // Actualizar spell words (follow player like chat bubbles)
    if (this.scene.activeSpellWords.size > 0) {
      this.scene.activeSpellWords.forEach((text, entityId) => {
        const render = this.scene.playerObjects.get(entityId);
        if (!render || !text.active) {
          text.destroy();
          this.scene.activeSpellWords.delete(entityId);
          return;
        }
        const offsetY = (text as any).offsetY ?? 0;
        // Pixel-snap X/Y igual que el body para evitar shimmer al caminar horizontal.
        text.setPosition(
          Math.round(render.currentWorldX),
          Math.round(getOverheadTextY(render.sprite.y, render.sprite.displayHeight) + offsetY)
        );
      });
    }

    // Actualizar error texts (follow local player)
    if (this.scene.activeErrorTexts.size > 0) {
      this.scene.activeErrorTexts.forEach((text, entityId) => {
        const render = this.scene.playerObjects.get(entityId);
        if (!render || !text.active) {
          text.destroy();
          this.scene.activeErrorTexts.delete(entityId);
          return;
        }
        const offsetY = (text as any).offsetY ?? 0;
        // Pixel-snap X/Y igual que el body para evitar shimmer al caminar horizontal.
        text.setPosition(
          Math.round(render.currentWorldX),
          Math.round(getOverheadTextY(render.sprite.y, render.sprite.displayHeight) + offsetY)
        );
      });
    }

    // Actualizar bubbles de NPCs

    if (this.scene.activeNpcChatBubbles.size > 0) {
      this.scene.activeNpcChatBubbles.forEach((text, npcId) => {
        const render = this.scene.npcObjects.get(npcId);

        if (!render || !text.active) {
          text.destroy();

          this.scene.activeNpcChatBubbles.delete(npcId);

          return;
        }

        // Usar altura real del sprite - talkOffset (negativo = más arriba)
        const bodyId = render.bodyId;
        const bodyHeight = render.sprite.displayHeight;
        const talkOffset = getTalkOffset(bodyId);

        const baseY = getOverheadTextY(render.sprite.y, bodyHeight, talkOffset);

        const offsetY = (text as any).offsetY ?? 0;

        text.setPosition(render.sprite.x, baseY + offsetY);
      });
    }

    // Actualizar textos de ganancia de HP/Mana
    if (this.scene.activeHealthManaGainTexts.size > 0) {
      this.scene.activeHealthManaGainTexts.forEach((text, trackingKey) => {
        const isPlayer = (text as any).isPlayer as boolean;
        const entityId = (text as any).entityId as string;
        
        const render = isPlayer 
          ? this.scene.playerObjects.get(entityId)
          : this.scene.npcObjects.get(entityId);

        if (!render || !text.active) {
          text.destroy();
          this.scene.activeHealthManaGainTexts.delete(trackingKey);
          return;
        }

        // Calcular posición base: usar la misma lógica que en showHealthManaGainText
        // sprite.y es la base de los pies, subimos solo unos pocos píxeles
        const baseY = render.sprite.y - 10; // Solo 10 píxeles desde los pies (parte muy baja del body, cerca del centro del tile)
        const offsetY = (text as any).offsetY ?? 0;

        // Usar currentWorldX para jugadores (interpolado), sprite.x para NPCs
        const x = isPlayer && 'currentWorldX' in render
          ? (render as PlayerRenderObjects).currentWorldX
          : render.sprite.x;

        const offsetX = (text as any).offsetX ?? 0;
        text.setPosition(x + offsetX, baseY + offsetY);
      });
    }

    // Actualizar textos de daño (mismo follow que las curas, pero a la izquierda)
    if (this.scene.activeDamageTexts.size > 0) {
      this.scene.activeDamageTexts.forEach((text, trackingKey) => {
        const isPlayer = (text as any).isPlayer as boolean;
        const entityId = (text as any).entityId as string;

        const render = isPlayer
          ? this.scene.playerObjects.get(entityId)
          : this.scene.npcObjects.get(entityId);

        if (!render || !text.active) {
          text.destroy();
          this.scene.activeDamageTexts.delete(trackingKey);
          return;
        }

        const baseY = render.sprite.y - 10;
        const offsetY = (text as any).offsetY ?? 0;
        const offsetX = (text as any).offsetX ?? 0;
        const x = isPlayer && 'currentWorldX' in render
          ? (render as PlayerRenderObjects).currentWorldX
          : render.sprite.x;

        text.setPosition(x + offsetX, baseY + offsetY);
      });
    }
  }

}
